<template>
  <div class="flex flex-col w-full border-b border-grey-academy-150">
    <div class="flex items-center w-full justify-between py-3
      text-sm lg:text-lg">
      <div class="flex flex-col px-2 lg:w-1/6 w-1/8 text-left font-black"
        v-if="lesson.status !== 'canceled'" >
        <span>{{ dayStart }}</span>
        <span>{{ hourStart }} - {{ hourEnd }} </span>
      </div>
      <span class="flex justify-start px-2 w-1/2 md:w-1/4"> {{ lesson.tier.name }} </span>
      <span class="px-2 w-1/6 hidden md:block" v-if="!showStartButton">
        {{ formatter.format(lesson.total_to_pay/100) }}
      </span>
      <div v-if="lesson.status !== 'canceled'" class="flex w-1/4 justify-end">
        <button v-if="showStartButton" class="bg-orange-academy-300 rounded p-1 text-white uppercase
        font-bold border-2 border-orange-academy-300 hover:bg-white hover:text-orange-academy-300
        hidden md:block mr-10"
        @click="startLesson" :class="isLoading ? 'loader' : ''">
          {{$t('lesson.start-lesson')}}
        </button>
        <button @click="open = !open">
          <arrow-down-icon class="w-6 lg:w-10" v-if="!open && !lesson.ended" />
        </button>
        <button @click="open = !open">
          <arrow-up-icon class="w-6 lg:w-10" v-if="open && !lesson.ended" />
        </button>
      </div>
      <div v-else class="uppercase font-bold text-red-500">
        {{ $t('lesson.canceled') }}
      </div>
    </div>
    <div v-if="open && !lesson.ended" class="pb-6 flex flex-col items-center">
      <button class="bg-orange-academy-300 rounded p-1 text-white uppercase
      font-bold border-2 border-orange-academy-300 hover:bg-white hover:text-orange-academy-300
      w-1/3  block md:hidden" v-if="showStartButton"
      @click="startLesson" :class="isLoading ? 'loader' : ''">
        {{$t('lesson.start-lesson')}}
      </button>
      <div class="w-full text-left flex items-center justify-between flex-wrap"
      v-if="lesson.comments">
        <div class="flex items-center text-xs md:text-base">
          <div class="m-2 font-bold">{{$t('lesson.yourComments')}}</div>
          <span>{{ lesson.comments }}</span>
        </div>
      </div>
      <div class="w-full text-left flex items-center justify-between flex-wrap">
        <div class="flex items-center text-xs md:text-base">
          <div class="m-2 font-bold">{{$t('lesson.lang')}}:</div>
          <span>{{ lesson.lang }}</span>
        </div>
      </div>
      <div class="w-full text-left flex items-center justify-between flex-wrap">
        <div class="flex items-center justify-start text-xs md:text-base">
          <div class="m-2 font-bold">{{$t('lesson.payment-method')}}</div>
          <lesson-payment-method :lesson="lesson" />
        </div>
        <span class="px-2 w-1/4 mt-2">
          <payment-received></payment-received>
        </span>
         <router-link class="bg-beige-academy-250 rounded p-1 hover:bg-beige-academy-300
          font-bold text-white w-full md:w-1/3 text-center mr-3 mt-2"
          :to="'/payment?booking='+lesson.booking_id" v-if="lesson.status !== 'paid'">
            {{$t('lesson.change-payment')}}
        </router-link>
      </div>
      <div class="flex w-full justify-end">
        <button v-if="showFreeCancelButton" @click="cancelLesson(true)"
        class="bg-orange-academy-300 rounded p-1 text-white uppercase
        font-bold border-2 border-orange-academy-300 hover:bg-white hover:text-orange-academy-300
        w-1/3  hidden md:block mr-3">
          {{ $t('lesson.freeCancelLesson') }}
        </button>
        <button v-if="showCancelButton" @click="cancelLesson(false)"
        class="bg-orange-academy-300 rounded p-1 text-white uppercase
        font-bold border-2 border-orange-academy-300 hover:bg-white hover:text-orange-academy-300
        w-1/3  hidden md:block mr-3">
          {{ $t('lesson.cancelLesson') }}
        </button>
      </div>
    </div>
    <div v-if="lesson.ended" class="text-left text-beige-academy-500">
      <span v-if="lesson.review" class="text-chocolate-academy-600">
        {{$t('lesson.review-score')}} {{lesson.review.teacher_avg}}
      </span>
      <router-link :to="'/lesson-review?booking='+lesson.lesson_id"
      v-if="!lesson.review && lesson.status !== 'canceled'">
        {{$t('lesson.review')}}
      </router-link>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import axios from 'axios';
import { getStudentUrl } from '@/services/LessonService';
// import LessonMixin from '@/mixins/LessonMixin';
import ArrowDownIcon from '../graphics/ArrowDownIcon.vue';
import ArrowUpIcon from '../graphics/ArrowUpIcon.vue';
import LessonPaymentMethod from '../payment/LessonPaymentMethod.vue';
import PaymentReceived from '../graphics/PaymentReceived.vue';
import AreYouSureToCancelLesson from '../modals/AreYouSureToCancelLesson.vue';

// eslint-disable-next-line no-undef
const stripe = Stripe(process.env.VUE_APP_STRIPE_KEY);

export default {
  props: ['lesson', 'teacher'],

  components: {
    ArrowDownIcon,
    ArrowUpIcon,
    LessonPaymentMethod,
    PaymentReceived,
  },

  data() {
    return {
      isLoading: false,

      open: false,
    };
  },

  computed: {
    showStartButton() {
      if (this.lesson.started && !this.lesson.ended) {
        return true;
      }
      return false;
    },
    showFreeCancelButton() {
      return dayjs.tz() < this.date.subtract(this.teacher.cancellation_deadline, 'h');
    },
    showCancelButton() {
      if (!this.showFreeCancelButton && !this.showStartButton) {
        return true;
      }
      return false;
    },

    dayStart() {
      return dayjs(this.date).tz(this.choosenTimezone).format('LL');
    },

    hourStart() {
      return dayjs(this.date).tz(this.choosenTimezone).format('HH:mm');
    },

    hourEnd() {
      return dayjs(this.dateEnd).tz(this.choosenTimezone).format('HH:mm');
    },

    date() {
      return this.lesson.slot ? dayjs.tz(this.lesson.slot.start_date, 'UTC') : null;
    },

    dateEnd() {
      return this.lesson.slot ? dayjs.tz(this.lesson.slot.end_date, 'UTC') : null;
    },
  },

  methods: {
    startLesson() {
      this.isLoading = true;
      this.payLesson();
    },

    getUrl() {
      getStudentUrl(this.lesson.booking_id).then(({ data }) => {
        window.location.href = data;
      }).catch(() => {
        this.createNotification('error', 'Non riusciamo a collegarti al meeting. Ritenta tra qualche minuto');
      });
    },

    payLesson() {
      axios.post(`/api/lessons/${this.lesson.booking_id}/payment`)
        .then((response) => {
          if (this.lesson.payment_method === 'card' && response.data.error) {
            this.checkTheErrorType(response.data, this.lesson.slot);
            return;
          }
          this.getUrl();
        }).catch(() => {
          this.createNotification('error', `${this.$t('notifications.errorPayment')}`);
          this.goUpdatePayment();
        });
    },

    checkTheErrorType(paymentResult, order) {
      if (paymentResult.paymentIntent) {
        this.getAuthenticationToConfirmPayment(paymentResult.paymentIntent, order);
        return;
      }
      this.isLoading = false;
      this.goUpdatePayment();
      this.createNotification('error', paymentResult.error.message);
    },

    getAuthenticationToConfirmPayment(paymentIntent) {
      this.message = this.$t('stripe.authRequired');

      stripe.confirmCardPayment(paymentIntent.client_secret, {
        payment_method: paymentIntent.last_payment_error.payment_method.id,
      }).then((result) => {
        if (result.error) {
          this.isLoading = false;
          this.createNotification('error', `${result.error.message}`);
          this.setLessonPaymentHasFailed();
          return;
        }
        this.setLessonHasBeenPaid(paymentIntent.amount);
      });
    },

    async setLessonHasBeenPaid(amount) {
      await axios.put(`/api/lessons/${this.lesson.lesson_id}/paid`, { amount });
      this.getUrl();
    },

    async setLessonPaymentHasFailed() {
      await axios.put(`/api/lessons/${this.lesson.lesson_id}/fail`);
      this.goUpdatePayment();
    },

    goUpdatePayment() {
      this.$router.push(`/failed-payment?lesson=${this.lesson.lesson_id}&classroom=${this.$route.params.slug}`);
    },

    cancelLesson(forFree) {
      this.$modal.show(AreYouSureToCancelLesson, {
        lesson: this.lesson,
        forFree,
      }, {
        height: 'auto',
        adaptive: true,
      });
    },

  },
};
</script>
